import type { SimpleMarketplaceResponse } from '@yoop/server-model/marketplace';
import { MarketplaceType } from '@yoop/server-model/marketplace';
import {
  Chance,
  OfferStatus as ServerOfferStatus,
  OfferSubStatus,
  type SimpleOfferResponse,
} from '@yoop/server-model/token-info';
import { datetime } from '@yoop/datetime';
import type { MyYoopOffers } from '@yoop/whitelabel-data';
import { OfferChance, OfferStatus } from '@yoop/whitelabel-data';
import isNil from 'ramda/src/isNil';

export const requestedOffersInfoFrom = (marketplace: SimpleMarketplaceResponse) => {
  const offers: MyYoopOffers[] = [];
  let canSubmitMoreOffers = false;
  const {
    id: marketplaceId,
    type,
    offerGroupInformation,
    parentMarketplaceId,
    userTicketLimitRemaining,
    endDateTime,
  } = marketplace;
  if (
    (type !== MarketplaceType.AUCTION && type !== MarketplaceType.DRAW) ||
    !isNil(parentMarketplaceId)
  ) {
    return { offers, canSubmitMoreOffers };
  }
  const marketplaceEnd = datetime.parse(endDateTime);
  if (datetime.isPast(marketplaceEnd)) {
    offerGroupInformation?.forEach(({ id: offerGroupId, description, activeOffer }) => {
      const offer = finishedRequestedOfferFrom({
        description,
        marketplaceType: type,
        activeOffer,
        marketplaceId,
        offerGroupId,
      });
      if (!isNil(offer)) {
        offers.push(offer);
      }
    });
  } else {
    offerGroupInformation?.forEach(({ id: offerGroupId, description, activeOffer }) => {
      if (activeOffer.offerStatus !== ServerOfferStatus.DEFAULT) {
        //active marketplace might have lost offers in case of fraud, etc.
        return;
      }
      canSubmitMoreOffers = canSubmitMoreOffers || userTicketLimitRemaining > 0;
      offers.push({
        id: parseInt(activeOffer.id),
        offerGroupId: parseInt(offerGroupId),
        price: activeOffer.amount ?? 0,
        transactingToolId: parseInt(marketplaceId),
        description,
        yoopCount: activeOffer.numberOfTokens,
        status: OfferStatus.Active,
        chance: type === MarketplaceType.AUCTION ? chanceFrom(activeOffer.chance) : undefined,
      });
    });
  }
  return { offers, canSubmitMoreOffers };
};

const finishedRequestedOfferFrom = ({
  activeOffer,
  offerGroupId,
  marketplaceId,
  marketplaceType,
  description,
}: {
  activeOffer: SimpleOfferResponse;
  marketplaceId: string;
  offerGroupId: string;
  description: string;
  marketplaceType: MarketplaceType;
}) => {
  if (!activeOffer || activeOffer.offerStatus === ServerOfferStatus.WON_TOKEN_ASSIGNED) {
    return undefined;
  }

  let actionEndTime: string;
  let status: OfferStatus = OfferStatus.PendingResult;
  switch (activeOffer.offerStatus) {
    case ServerOfferStatus.LOST:
      status = OfferStatus.Lost;
      break;
    case ServerOfferStatus.WON_MANUAL_PAYMENT_REQUIRED:
      actionEndTime = activeOffer.retryEndTime;
      if (activeOffer.offerSubStatus === OfferSubStatus.AUTO_PAYMENT_FAILED) {
        status = OfferStatus.PaymentFailed;
      } else {
        status = OfferStatus.PendingPayment;
      }
      break;
    case ServerOfferStatus.WON_PAYMENT_SUCCESSFUL:
      actionEndTime = activeOffer.retryEndTime;
      status = OfferStatus.PendingClaim;
      break;
    default:
  }
  return {
    id: parseInt(activeOffer.id),
    offerGroupId: parseInt(offerGroupId),
    price: activeOffer.amount ?? 0,
    transactingToolId: parseInt(marketplaceId),
    description,
    yoopCount: activeOffer.numberOfTokens,
    actionEndTime,
    status,
    chance:
      marketplaceType === MarketplaceType.AUCTION ? chanceFrom(activeOffer.chance) : undefined,
  };
};

const chanceFrom = (chance: Chance): OfferChance => {
  switch (chance) {
    case Chance.OK:
      return OfferChance.Ok;
    case Chance.GOOD:
      return OfferChance.Good;
    case Chance.GREAT:
      return OfferChance.Great;
    case Chance.NEGLIGIBLE:
      return OfferChance.Negligible;
    case Chance.POOR:
      return OfferChance.Poor;
  }
};
